import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { List } from 'antd';
import classNames from 'classnames';
import copy from 'copy-to-clipboard';
import { isEmpty } from 'lodash';
import React, { forwardRef, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Button, Checkbox, Form, MessagePlugin, Popconfirm, Tooltip } from 'tdesign-react';

import { IFormItemProps } from '@/components';
import { EditorCopy, EditorDel, EditorDrag } from '@/components/icon';
import { ActionType, ChangeMediaType, EventToolBarType, EventType, IEventVoiceList, MissionType, RELATEDMESSAGE } from '@/const/mission';
import { AppData } from '@/editor/flowEditor/control/types';
import { IEvents, StoryLayout } from '@/interface';
import { ThumbnailType } from '@/interface/media';
import { arrayReducerByKeyIndex, reducerActions } from '@/reducer/arrayReducers';
import { useAppDispatch, useTypedSelector } from '@/store';
import { changeMissionPreview } from '@/store/modules/preview';

import Empty from '../empty';
import styles from './index.module.less';
import ItemComp from './Item';
import ExclItem from './Item/excl';
import EventToolBar from './toolbar';
import { ToolbarItem } from './toolbar/toolbar';
import { handleExportEvents } from './util/exportEvent';

const { FormItem } = Form;

interface IProps {
  id: string,
  params: {
    val: IEvents;
    batchVisible: boolean;
    batchCheckList: string[];
    handleCheck: (keyIndex: string) => void;
    drag: boolean;
    disabled: boolean;
    index: number;
    handleChange: (value: {
      [key: string]: any;
    }, index: number, addItem?: boolean) => void
    compValue: any;
    botList: { name: string, id: string, url: string }[];
    voiceList: IEventVoiceList[];
    propsList: { name: string, id: string }[];
    sceneId?: string;
    fieldname: string;
    missionType: MissionType;
    showSystem: boolean;
    isMergeSceneMission?: boolean;
    isHorizontal: boolean;
    contentEventIdColWidth: number;
    copyDisabled: boolean;
    copyItem: (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>, value: IEvents) => void
    removeItem: (index: number, event: React.MouseEvent<HTMLElement, MouseEvent>, keyIndex: string) => void
    toolBar: ToolbarItem[];
  }
}

interface IFormArrayProps {
  fieldsConfig: IFormItemProps[];
  form: any;
  repeatConfig?: {
    [key: string]: IFormItemProps[];
  };
  hasExcl: boolean;
  hasParse: boolean,
  showSystem: boolean, // 是否显示旁白
  toolBar?: ToolbarItem[];
  cparams: {
    sceneId?: string;
    isStart: boolean;
    shareInfo: AppData<Partial<unknown>>['data']['shareInfo'];
    completionTipValidate?: { [key: string]: boolean }
    missionType: MissionType;
    sceneList: { name: string, id: string }[];
    propsList: { name: string, id: string }[];
    botList: { name: string, id: string, url: string }[];
    voiceList: IEventVoiceList[];
    showError: boolean;
    isMergeSceneMission?: boolean;
    isHorizontal?: boolean;
  }
}

// 可排序项组件
const SortableItem = forwardRef(({ id, params }: IProps, _ref) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform ? { x: transform.x, y: transform.y, scaleX: 1, scaleY: 1 } : null),
    opacity: isDragging ? 0.8 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  const {
    val,
    batchVisible,
    batchCheckList,
    handleCheck,
    drag,
    disabled,
    index,
    handleChange,
    compValue,
    botList,
    voiceList,
    propsList,
    sceneId,
    fieldname,
    missionType,
    showSystem,
    isMergeSceneMission,
    isHorizontal,
    contentEventIdColWidth,
    copyDisabled,
    copyItem,
    removeItem,
    toolBar,
  } = params;


  if (!id) {
    return;
  }

  const thumbnail: ThumbnailType = useMemo(() => {
    if (val?.action?.changeMedia?.type !== ChangeMediaType.CHANGE_MEDIA_TYPE_MULTI_CHARACTER) {
      return {};
    }

    for (let i = index - 1; i >= 0; i--) {
      const preMedia = compValue[i]?.action?.changeMedia;

      if (preMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_BACKGROUND_IMAGE) {
        return {
          url: preMedia?.backgroundImage?.url?.url || '',
          srcType: 'image',
        };
      }
      if (preMedia?.type === ChangeMediaType.CHANGE_MEDIA_TYPE_BACKGROUND_VIDEO) {
        const thumbnail = preMedia?.backgroundVideo?.thumbnail;
        if (thumbnail) {
          return {
            url: thumbnail,
            srcType: 'image',
          };
        }
        return {
          url: preMedia?.backgroundVideo?.url?.url || '',
          srcType: 'video',
        };
      }
    }

    return {};
  }, [compValue, index, val?.action?.changeMedia?.type]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={e => e.stopPropagation()}
    >
      <List.Item key={val?.keyIndex} style={{ padding: '4px 0px' }}>
        <FormItem
          className={classNames('custom-form-group', styles.group)}>
          <div className={styles.drag}>
            {
              batchVisible ? <div className={styles.checkbox}>
                <Checkbox
                  style={{ borderRadius: 2, borderRight: 2 }}
                  checked={batchCheckList.includes(val?.keyIndex as unknown as string)}
                  onClick={() => handleCheck(val?.keyIndex as unknown as string)}
                />
              </div> : <>
                <div className={styles.drag}>
                  {drag && !disabled && (
                    <div className={classNames('draggable', styles.drag)} {...listeners}>
                      <EditorDrag />
                    </div>
                  )}
                </div>
              </>
            }
          </div>
          <FormItem
            className={classNames(styles.item)}
          >
            <div style={{ flexGrow: '1', minWidth: 0 }}>
              <ItemComp
                index={index}
                onChange={value => handleChange({ ...value, keyIndex: val?.keyIndex }, index, false)}
                value={val}
                arrayValue={compValue}
                disabled={disabled || batchVisible}
                botList={botList}
                voiceList={voiceList}
                propsList={propsList}
                sceneId={sceneId}
                fieldname={fieldname}
                missionType={missionType}
                showSystem={showSystem}
                isMergeSceneMission={isMergeSceneMission}
                isHorizontal={isHorizontal}
                contentEventIdColWidth={contentEventIdColWidth}
                thumbnail={thumbnail}
                onEnter={value => handleChange({ ...value, keyIndex: val?.keyIndex }, index, true)}
              />
            </div>
          </FormItem>
          <div className={styles.options}>
            <Tooltip
              overlayClassName={styles.toolbarTooltip}
              content={'复制'}
              placement="top"
              showArrow
              destroyOnClose
            >
              <Button
                className={classNames(styles.add, {
                  'nova-disabled-opacity': copyDisabled || batchVisible,
                })}
                theme="default"
                disabled={copyDisabled || batchVisible}
                onClick={event => copyItem(index, event, compValue[index])
                }
              >
                <EditorCopy />
              </Button>
            </Tooltip>
            <Tooltip
              overlayClassName={styles.toolbarTooltip}
              content={'删除'}
              placement="top"
              showArrow
              destroyOnClose
            >
              <Button
                className={classNames(styles.delete, {
                  'nova-disabled-opacity': disabled || batchVisible,
                })}
                disabled={disabled || batchVisible}
                theme="default"
                onClick={event => removeItem(index, event, val?.keyIndex as unknown as string)}
              >
                <EditorDel />
              </Button>
            </Tooltip>
            <EventToolBar
              onChange={(data: any) => handleChange(data, index, true)}
              values={compValue}
              disabled={disabled || batchVisible}
              botList={botList}
              missionType={missionType}
              toolBar={toolBar}
              fieldname={fieldname}
            />
          </div>
        </FormItem>
        <div>
        </div>
      </List.Item>
    </div>
  );
});

// 基础组件
export const CustomArrayEvents: React.FC<
{
  value: IEvents[];
  fieldname: string;
  config: any;
  errors: { [key: string]: any };
  max: number;
  drag: boolean;
  disabled: boolean;
  missionType: MissionType;
  onChange: (value: string[] | string) => {};
} & IFormArrayProps
> = (props) => {
  const {
    drag = false,
    value = [],
    onChange,
    disabled = false,
    cparams,
    hasExcl = false,
    hasParse = false,
    showSystem = false,
    fieldname,
  } = props;

  const {
    shareInfo, sceneId, missionType, propsList = [],
    botList = [], isMergeSceneMission, isHorizontal = false,
    voiceList = [],
  } = cparams || {};

  const toolBar = (props.toolBar || []).filter((e) => {
    if (typeof e.visible === 'function') {
      return e.visible(isHorizontal);
    }
    return true;
  });

  const [arrayReducerData, dispatch] = useReducer(arrayReducerByKeyIndex, { items: [] });
  const appDispatch = useAppDispatch();
  const { mission } = useTypedSelector(store => store.preview);
  const { storyInfo } = useTypedSelector(store => store.story);

  const [compValue, setCompValue] = useState<IEvents[]>([]);

  const [copySuccess, setCopySuccess] = useState<boolean | undefined>(undefined);

  const handleClick = useCallback(() => {
    appDispatch(changeMissionPreview({
      scene: shareInfo?.scene || {}, mission: value,
    }));
  }, [value, shareInfo]);

  useEffect(() => {
    if (arrayReducerData?.changeNumber) {
      onChange(arrayReducerData?.items);
    }
  }, [arrayReducerData?.changeNumber]);

  useEffect(() => {
    dispatch({
      type: reducerActions.INIT_EMPTY,
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(value)) {
      if (arrayReducerData?.items.length === 0) {
        dispatch({
          type: reducerActions.CHANGE_ALL,
          payload: {
            value: [...value],
          },
        });
      }
      const result = value?.filter(e => e?.action?.type !== ActionType.ACTION_TYPE_GUIDEREPLY);
      setCompValue(result);
    } else {
      setCompValue([]);
    }
  }, [value]);

  const changeArrayInfoObject = useCallback(
    (key: number, value: { [key: string]: unknown }) => {
      dispatch({
        type: reducerActions.UPDATE_ITEM,
        payload: {
          index: key,
          value,
          keyIndex: value?.keyIndex as string,
        },
      });
    },
    [compValue],
  );

  const removeItem = (
    index: number,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    keyIndex: string,
  ) => {
    event?.stopPropagation();
    dispatch({
      type: reducerActions.REMOVE_ITEM,
      payload: {
        index,
        keyIndex,
      },
    });
  };

  const copyItem = (
    index: number,
    event: React.MouseEvent<HTMLElement, MouseEvent>,
    value: IEvents,
  ) => {
    event.stopPropagation();
    dispatch({
      type: reducerActions.ADD_ITEM,
      payload: {
        index: index + 1,
        value: {
          ...value,
          id: '', // 重置id
          keyIndex: undefined,
        },
      },
    });
  };

  const handleChange = useCallback(
    (
      value: { [key: string]: any },
      index: number,
      addItem?: boolean,
    ) => {
      if (value?.type === EventToolBarType.ADD_DRAWER) {
        return;
      }
      if (addItem) {
        dispatch({
          type: reducerActions.ADD_ITEM,
          payload: {
            index: index + 1,
            value: {
              ...value,
              keyIndex: undefined,
            },
          },
        });
      } else {
        changeArrayInfoObject(index, value);
      }
    },
    [arrayReducerData],
  );

  const handleChange2 = useCallback((val: { value: IEvents[] }) => {
    dispatch({
      type: reducerActions.UPDATE_ITEM,
      payload: {
        value: val.value,
        append: true,
      },
    });
  }, [fieldname]);


  const handleDelete = () => {
    dispatch({
      type: reducerActions.BATCH_DELETE,
      payload: {
        batchDelete: {
          keyIndexs: batchCheckList,
        },
      },
    });
    closeBatch();
  };

  const handleExport = () => {
    // 格式化剧情内容
    const formattedContent = handleExportEvents(
      compValue,
      batchCheckList,
      botList,
      propsList,
      true,
    )?.join('\n'); // 使用两个换行符分隔不同的剧情内容
    const copySuccess = copy(formattedContent, { format: 'text/plain' });

    if (copySuccess) {
      setCopySuccess(true);
      MessagePlugin.success('已成功复制到剪贴板，请粘贴使用');
    } else {
      setCopySuccess(false);
      MessagePlugin.error('复制失败');
    }
  };

  const [batchVisible, setBatchVisible] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [batchCheckList, setBatchCheckList] = useState<string[]>([]);

  const openBatch = () => {
    setBatchVisible(true);
  };

  const closeBatch = () => {
    setBatchVisible(false);
    setCheckAll(false);
    setBatchCheckList([]);
    setCopySuccess(undefined);
  };

  const handleCheck = (keyIndex: string) => {
    if (keyIndex) {
      const index = batchCheckList.indexOf(keyIndex);
      if (index > -1) {
        batchCheckList.splice(index, 1);
      } else {
        batchCheckList.push(keyIndex);
      }
      setBatchCheckList([...batchCheckList]);
    }
  };

  const handleCheckAll = () => {
    setCheckAll((pre) => {
      if (!pre) {
        const keyIndexs = compValue.map(item => item?.keyIndex).filter(item => item !== undefined);
        setBatchCheckList(keyIndexs as string[]);
        return !pre;
      }
      setBatchCheckList([]);
      return !pre;
    });
  };

  const showBatchOption = useMemo(() => {
    if (fieldname === RELATEDMESSAGE) {
      return false;
    }
    return true;
  }, [fieldname, missionType]);

  const width = document.getElementById('contentEventIdColWidth')?.offsetWidth || 600;

  const contentEventIdColWidth = useMemo(() => width, [width]);

  // 配置拖拽传感器
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // 处理拖拽结束
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = compValue.findIndex(item => item.keyIndex === active.id);
      const newIndex = compValue.findIndex(item => item.keyIndex === over.id);
      const newItems = arrayMove(compValue, oldIndex, newIndex);
      // sortData(oldIndex, newIndex);
      dispatch({
        type: reducerActions.CHANGE_ALL,
        payload: {
          value: newItems,
        },
      });
    }
  }, [compValue]);

  const [height, setHeight] = useState(200);

  return (
    <div className={styles.arrayWrap}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.contentEvent} id="contentEventIdColWidth">
          <SortableContext
            items={compValue.map(item => item.keyIndex || '')}
            strategy={verticalListSortingStrategy}
          >
            <>
              {(hasExcl || hasParse) && (
                <div style={{ marginBottom: 20 }}>
                  <ExclItem
                    propsList={propsList}
                    shareInfo={shareInfo}
                    botList={botList}
                    disabled={disabled}
                    isMergeSceneMission={isMergeSceneMission}
                    onChange={val => handleChange2(val)}
                    hasExcl={hasExcl}
                    hasParse={hasParse}
                    isHorizontal={isHorizontal}
                    buttonDisabled={batchVisible}
                    toolBar={toolBar}
                  />
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  {
                    (disabled || isEmpty(compValue)) || !showBatchOption ? null
                      : <div>
                        {
                          !batchVisible
                            ? <div className={styles.batchOptions}>
                              <Button theme='default'
                                size="small"
                                disabled={disabled}
                                style={{ fontSize: 13 }}
                                onClick={openBatch}>批量操作</Button>
                            </div>
                            : <div className={styles.batchOptionsVisible}>
                              <div className={styles.left}>
                                <Checkbox
                                  style={{ fontSize: 13, marginRight: 5 }}
                                  onClick={handleCheckAll}
                                  checked={checkAll}
                                  disabled={disabled || isEmpty(compValue)}
                                >
                                  全选
                                </Checkbox>
                              </div>
                              <div className={styles.right}>
                                <Popconfirm
                                  content="删除后不可恢复，确认删除吗"
                                  destroyOnClose
                                  placement="top"
                                  showArrow
                                  theme="warning"
                                  onConfirm={handleDelete}
                                >
                                  <Button
                                    theme='default'
                                    size="small"
                                    style={{ fontSize: 13, marginRight: 5 }}
                                    disabled={disabled || isEmpty(batchCheckList)}
                                  >
                                    批量删除
                                  </Button>
                                </Popconfirm>
                                <Tooltip
                                  content={<div>
                                    {
                                      copySuccess === undefined ? <div>
                                        <p>仅支持复制剧情文本内容，如有其他配置项，请单独设置</p>
                                        <p>剧情内容将复制至剪切板</p></div>
                                        : copySuccess ? <div>
                                          <p>成功复制剧情内容至剪切板</p>
                                          <p>仅支持复制剧情文本内容，如有其他配置项，请单独设置</p>
                                        </div>
                                          : '复制失败-请重新复制/使用最新版本chrome浏览器复制'
                                    }
                                  </div>}
                                  destroyOnClose
                                  showArrow
                                  theme="default"
                                >
                                  <Button
                                    theme='default'
                                    onClick={handleExport}
                                    size="small"
                                    style={{ fontSize: 13, marginRight: 5 }}
                                    disabled={disabled || isEmpty(batchCheckList)}
                                  >
                                    批量复制
                                  </Button>
                                </Tooltip>

                                <Button theme='default'
                                  size="small"
                                  style={{ fontSize: 13 }}
                                  onClick={closeBatch}>取消</Button>
                              </div >
                            </div >
                        }
                      </div >
                  }
                </div >
                {showBatchOption && storyInfo.layout === StoryLayout.LAYOUT_VERTICAL && <Button size="small"
                  disabled={mission != null}
                  style={{ fontSize: 13, marginLeft: 5, marginBottom: 16 }}
                  onClick={handleClick}>
                  预览剧情
                </Button>}
              </div >
              <div
                style={{ height: '100%', overflow: 'hidden', boxSizing: 'border-box' }}
              >
                {
                  compValue?.length > 0
                    ? <List>
                      <Virtuoso
                        style={{
                          minHeight: 200,
                          height,
                          paddingRight: 3,
                          maxHeight: 600,
                        }}
                        totalListHeightChanged={(h) => {
                          setHeight(h);
                        }}
                        data={compValue}
                        itemContent={(index, val) => {
                          const copyDisabled = disabled
                            || compValue[index]?.type === EventType.EVENT_TYPE_EXCL;
                          return itemContent({
                            compValue,
                            val,
                            index,
                            batchCheckList,
                            batchVisible,
                            drag,
                            disabled,
                            handleChange,
                            removeItem,
                            copyItem,
                            copyDisabled,
                            botList,
                            voiceList,
                            missionType,
                            toolBar,
                            fieldname,
                            showSystem,
                            isHorizontal,
                            isMergeSceneMission,
                            sceneId,
                            propsList,
                            contentEventIdColWidth,
                            handleCheck,
                          });
                        }}
                      />
                    </List>
                    : <div className={styles.emptyBox}>
                      <Empty />
                      <EventToolBar
                        onChange={(data: any) => handleChange(data, 0, true)}
                        values={compValue}
                        disabled={disabled}
                        botList={botList}
                        missionType={missionType}
                        toolBar={toolBar}
                        addText="新增"
                        fieldname={fieldname}
                      />
                    </div>
                }
              </div>
            </>
          </SortableContext >
        </div >
      </DndContext >
    </div >
  );
};

// 子项目
const itemContent = (params: IProps['params']) => {
  const { compValue, val, index, disabled } = params;
  const copyDisabled = disabled
    || compValue[index]?.type === EventType.EVENT_TYPE_EXCL;
  const id = val?.keyIndex || `${index}`;
  return (
    <SortableItem key={id} id={id} params={{ ...params, copyDisabled }}
    />
  );
};
